import { useState } from 'react';
import { Link, useParams } from 'react-router';
import {
  ACTIVE_WORK_STATUSES,
  type OrderDetail,
  ORDER_KIND_LABEL,
  RACK_TYPE_SHORT,
  stagesFor,
  statusMeta,
  todayIso,
} from '@reikraft/shared';
import { ErrorBox, Icon, Loader, Notice, OverdueBadge, StageBar, StagesList, StatusBadge } from '@reikraft/ui';
import { api } from '../api';
import { Menu, PageHead } from '../components/common';
import { PaymentModal } from '../components/PaymentModal';
import { useFlag, useTz } from '../session';
import { ApproveModal, BenchModal, CancelModal, DeclineModal, EstimateLinkModal, IntakeModal } from './modals';
import {
  BenchPanel,
  ClientPanel,
  EstimatePanel,
  ExecutionPanel,
  FundPanel,
  JournalPanel,
  PartsPanel,
  PaymentsPanel,
  PhotosPanel,
  ProductPanel,
  WorksPanel,
} from './panels';
import { orderApi, useOrder, useOrderAction } from './useOrder';
import { UnitPicker } from '../components/common';

type ModalKind = 'intake' | 'approve' | 'decline' | 'cancel' | 'bench' | 'reserve' | 'issue' | 'payment' | 'exchange' | null;

export function OrderPage() {
  const { id = '' } = useParams();
  const q = useOrder(id);
  const [modal, setModal] = useState<ModalKind>(null);
  const [link, setLink] = useState<string | null>(null);
  const tz = useTz();
  const inventory = useFlag('inventory');

  const status = useOrderAction(
    id,
    (to: string) => orderApi.status(id, to),
    (o) => `Этап: ${statusMeta(o.status, o.kind).name}`,
  );
  const send = useOrderAction(
    id,
    async () => {
      const r = await api.post<{ order: OrderDetail; link: string | null }>(`/admin/orders/${id}/estimate/send`);
      if (r.link) setLink(r.link);
      return r;
    },
    'Смета отправлена клиенту',
  );
  const reserve = useOrderAction(
    id,
    (unitId: string) => api.post<OrderDetail>(`/admin/orders/${id}/reserve`, { unitId }),
    'Рейка зарезервирована',
  );
  const issue = useOrderAction(
    id,
    (unitId: string | null) => api.post<OrderDetail>(`/admin/orders/${id}/issue`, { unitId }),
    'Рейка из фонда выдана клиенту',
  );
  const toExchange = useOrderAction(
    id,
    (unitId: string | null) => api.post<OrderDetail>(`/admin/orders/${id}/exchange`, { unitId }),
    'Заказ оформлен как обмен',
  );

  if (q.isLoading) return <Loader />;
  if (q.isError || !q.data) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  const o = q.data;
  const meta = statusMeta(o.status, o.kind);
  const stages = stagesFor(o.kind);
  const idx = stages.indexOf(o.status);
  const prev = idx > 1 && o.status !== 'done' ? stages[idx - 1] : null;
  const active = ACTIVE_WORK_STATUSES.includes(o.status);
  const debt = (o.totalKop ?? 0) - o.paidKop;
  const busy = status.isPending || send.isPending || reserve.isPending || issue.isPending || toExchange.isPending;
  const overdueDays = o.overdue && o.dueDate ? Math.max(1, Math.round((Date.parse(todayIso(tz)) - Date.parse(o.dueDate)) / 86400000)) : 0;

  // главная кнопка зависит от этапа и типа заказа
  let primary: { label: string; run: () => void; disabled?: boolean } | null = null;
  const go = (to: string) => () => status.mutate(to);
  if (o.status === 'new' || o.status === 'pickup') {
    if (o.kind === 'sale') primary = { label: 'Зарезервировать рейку', run: () => setModal('reserve') };
    else primary = { label: 'Принять рейку', run: () => setModal('intake') };
  } else if (o.status === 'intake') {
    primary = { label: 'Начать диагностику →', run: go('diag') };
  } else if (o.status === 'diag') {
    primary =
      o.kind === 'repair'
        ? { label: 'Отправить смету клиенту', run: () => send.mutate(), disabled: !o.estimate.lines.length }
        : { label: 'Этап «Ремонт» →', run: go('repair') };
  } else if (o.status === 'approve') {
    primary = { label: 'Клиент согласовал →', run: () => setModal('approve') };
  } else if (o.status === 'repair') {
    primary = { label: 'Протокол стенда →', run: () => setModal('bench') };
  } else if (o.status === 'stand') {
    primary = o.kind === 'repair' ? { label: 'Готов к выдаче →', run: go('ready') } : { label: 'Оприходовать на склад →', run: go('done') };
  } else if (o.status === 'ready') {
    primary = { label: o.kind === 'sale' ? 'Выдать и закрыть →' : 'Выдать клиенту →', run: go('done') };
  } else if (o.status === 'done' && debt > 0) {
    primary = { label: 'Принять оплату', run: () => setModal('payment') };
  }

  const canCancel =
    ['new', 'pickup'].includes(o.status) ||
    (o.kind === 'sale' && o.status === 'ready') ||
    (o.kind !== 'repair' && o.kind !== 'sale' && active);
  const canDecline = o.kind === 'repair' && ['intake', 'diag', 'approve'].includes(o.status);
  const canExchange = o.kind === 'repair' && (active || ['new', 'pickup'].includes(o.status));
  const print = (path: string) => window.open(`/api/print/orders/${o.id}/${path}`, '_blank', 'noopener');

  const secondary = (
    <>
      {o.status === 'new' && o.kind !== 'sale' && !o.needsPickup && (
        <button type="button" className="rk-btn" onClick={go('pickup')} disabled={busy}>
          <Icon name="truck" size={16} /> Забрать у клиента
        </button>
      )}
      {o.status === 'diag' && o.kind === 'repair' && o.estimate.lines.length > 0 && (
        <button type="button" className="rk-btn" onClick={() => setModal('approve')} disabled={busy}>
          Согласовано по телефону
        </button>
      )}
      {o.status === 'approve' && (
        <>
          <button type="button" className="rk-btn rk-btn--warn" onClick={() => send.mutate()} disabled={busy}>
            Отправить смету повторно
          </button>
          <button type="button" className="rk-btn" onClick={() => setModal('decline')} disabled={busy}>
            Отказ клиента
          </button>
        </>
      )}
      {o.status === 'done' && o.kind !== 'sale' && o.kind !== 'repair' && o.unit && !o.unit.productSku && (
        <Link to={`/stock/new?unit=${o.unit.id}`} className="rk-btn">
          Выставить на продажу →
        </Link>
      )}
    </>
  );

  return (
    <>
      <PageHead
        crumbs={[{ label: 'Заказы', to: '/orders' }, { label: o.number }]}
        title={
          <div className="rk-row" style={{ ['--rk-gap' as string]: '12px 16px' }}>
            <h1 className="rk-mono" style={{ margin: 0, fontWeight: 700, fontSize: 28, letterSpacing: '0.02em' }}>
              {o.number}
            </h1>
            <StatusBadge status={o.status} kind={o.kind} size="lg" />
            {o.overdue && <OverdueBadge days={overdueDays} />}
            <span className="adm-head__sub">
              {o.organization ? 'Организация' : o.customer ? 'Частное лицо' : 'Фонд'} · {ORDER_KIND_LABEL[o.kind]}
              {o.rackType ? ` · ${RACK_TYPE_SHORT[o.rackType]}` : ''}
              {o.masterName ? ` · ${o.masterName}` : ''}
            </span>
          </div>
        }
        actions={
          <>
            <Menu
              label="Печать и другие действия"
              button={
                <>
                  <Icon name="print" size={16} /> Документы
                </>
              }
            >
              {o.receivedAt && (
                <button type="button" onClick={() => print('intake')}>
                  Акт приёмки
                </button>
              )}
              {o.bench.length > 0 && (
                <button type="button" onClick={() => print('bench')}>
                  Протокол стенда
                </button>
              )}
              {(o.status === 'ready' || o.status === 'done' || o.status === 'declined') && (
                <button type="button" onClick={() => print('act')}>
                  Акт выполненных работ
                </button>
              )}
              {o.status === 'done' && o.kind !== 'stock' && (
                <button type="button" onClick={() => print('warranty')}>
                  Гарантийный талон
                </button>
              )}
              {o.unit && (
                <button type="button" onClick={() => window.open(`/api/print/units/${o.unit!.id}/label`, '_blank', 'noopener')}>
                  Бирка с QR · {o.unit.code}
                </button>
              )}
              {prev && active && (
                <button type="button" onClick={go(prev)}>
                  ← Вернуть на этап «{statusMeta(prev, o.kind).name}»
                </button>
              )}
              {canExchange && (
                <button type="button" onClick={() => setModal('exchange')}>
                  Оформить как обмен
                </button>
              )}
              {canDecline && o.status !== 'approve' && (
                <button type="button" onClick={() => setModal('decline')}>
                  Отказ клиента от ремонта
                </button>
              )}
              {canCancel && (
                <button type="button" onClick={() => setModal('cancel')}>
                  Отменить заказ
                </button>
              )}
            </Menu>
            <span className="adm-hide-sm rk-row" style={{ ['--rk-gap' as string]: '8px' }}>
              {secondary}
              {primary && (
                <button type="button" className="rk-btn rk-btn--primary" onClick={primary.run} disabled={busy || primary.disabled}>
                  {primary.label}
                </button>
              )}
            </span>
          </>
        }
      />

      {o.status === 'cancelled' && <Notice label="Заказ отменён">{o.cancelReason ?? 'Причина не указана'}</Notice>}
      {o.status === 'declined' && (
        <Notice label="Отказ от ремонта" dot="var(--muted-2)">
          {o.cancelReason ?? 'Клиент отказался'}. Рейка {o.unit?.state === 'at_client' ? 'возвращается клиенту' : 'выкуплена в фонд'}.
        </Notice>
      )}
      {o.estimate.status === 'draft' && o.status === 'repair' && o.kind === 'repair' && (
        <Notice
          tone="warn"
          label="Смета изменилась"
          actions={
            <button type="button" className="rk-btn rk-btn--warn rk-btn--sm" onClick={() => send.mutate()}>
              Отправить клиенту
            </button>
          }
        >
          После согласования добавились работы — согласуйте новую сумму с клиентом.
        </Notice>
      )}

      {stages.length > 2 && (
        <div className="adm-hide-sm">
          <StagesList stages={o.stages} tz={tz} />
        </div>
      )}
      <div className={stages.length > 2 ? 'adm-show-sm rk-stack' : 'rk-stack'} style={{ ['--rk-gap' as string]: '8px' }}>
        <div className="rk-row rk-mono rk-muted" style={{ justifyContent: 'space-between', fontSize: 11 }}>
          <span>
            ЭТАП {Math.max(idx + 1, 0) || '—'} / {stages.length}
          </span>
          <span>{meta.name}</span>
        </div>
        <StageBar kind={o.kind} status={o.status} fluid />
      </div>

      <div className="adm-cols">
        <div className="adm-cols__main">
          <ProductPanel order={o} />
          {o.kind !== 'sale' && <EstimatePanel order={o} />}
          {o.kind !== 'sale' && ['repair', 'stand'].includes(o.status) && <WorksPanel order={o} />}
          {o.kind !== 'sale' && <PhotosPanel order={o} />}
          {o.kind !== 'sale' && (o.bench.length > 0 || ['repair', 'stand'].includes(o.status)) && (
            <BenchPanel order={o} onNew={() => setModal('bench')} />
          )}
          {inventory && o.kind !== 'sale' && <PartsPanel order={o} />}
        </div>
        <div className="adm-cols__side">
          <ClientPanel order={o} />
          <ExecutionPanel order={o} />
          <PaymentsPanel order={o} onPay={() => setModal('payment')} />
          <FundPanel
            order={o}
            onReserve={() => setModal('reserve')}
            onIssue={() => (o.issuedUnit ? issue.mutate(null) : setModal('issue'))}
            onExchange={() => setModal('exchange')}
            busy={busy}
          />
          <JournalPanel order={o} />
        </div>
      </div>

      {(primary || o.status === 'approve' || o.status === 'diag') && (
        <div className="adm-actionbar adm-show-sm">
          {primary && (
            <button type="button" className="rk-btn rk-btn--primary rk-btn--lg" onClick={primary.run} disabled={busy || primary.disabled}>
              {primary.label}
            </button>
          )}
          {o.status === 'repair' && o.kind === 'repair' && (
            <a href="#estimate" className="rk-btn rk-btn--warn">
              Нужна доп. работа → смета клиенту
            </a>
          )}
          <div className="rk-row" style={{ ['--rk-gap' as string]: '8px' }}>
            {secondary}
          </div>
        </div>
      )}

      <IntakeModal order={o} open={modal === 'intake'} onClose={() => setModal(null)} />
      <ApproveModal order={o} open={modal === 'approve'} onClose={() => setModal(null)} />
      <DeclineModal order={o} open={modal === 'decline'} onClose={() => setModal(null)} />
      <CancelModal order={o} open={modal === 'cancel'} onClose={() => setModal(null)} />
      <BenchModal order={o} open={modal === 'bench'} onClose={() => setModal(null)} />
      <EstimateLinkModal link={link} onClose={() => setLink(null)} />
      <UnitPicker
        open={modal === 'reserve' || modal === 'issue' || modal === 'exchange'}
        onClose={() => setModal(null)}
        initialQuery={o.product?.sku ?? o.car?.split(' ').slice(0, 2).join(' ') ?? ''}
        title={modal === 'exchange' ? 'Какую рейку из фонда отдаём клиенту' : 'Рейка из обменного фонда'}
        onPick={(u) => {
          const m = modal;
          setModal(null);
          if (m === 'reserve') reserve.mutate(u.id);
          if (m === 'issue') issue.mutate(u.id);
          if (m === 'exchange') toExchange.mutate(u.id);
        }}
      />
      <PaymentModal
        open={modal === 'payment'}
        onClose={() => setModal(null)}
        party={
          o.organization
            ? { kind: 'organization', id: o.organization.id, name: o.organization.name }
            : o.customer
              ? { kind: 'customer', id: o.customer.id, name: o.customer.name }
              : null
        }
        defaultOrderId={o.id}
        defaultAmountKop={Math.max(0, debt)}
      />
    </>
  );
}
